import { Box, SwipeableDrawer as MuiSwipeableDrawer, SxProps } from '@mui/material'
import useDrawerBackBehavior from './useDrawerBackBehavior'

/**
 * For back navigation to work properly, ensure this component always mounts from the parent. Control its visibility using the open prop, and render its children conditionally as needed.
 */

const SwipeableDrawerWithBackNavigation = ({
    open,
    anchor = 'bottom',
    disableSwipeToOpen = false,
    onClose,
    onOpen = () => {},
    children,
    enableBackNavigation = true,
    isLoginFlow = false,
    sx = {},
    keepMounted = false
}: {
    open: boolean
    anchor?: 'bottom' | 'top' | 'left' | 'right'
    disableSwipeToOpen?: boolean
    onOpen?: () => void
    onClose: () => void
    children: React.ReactNode
    enableBackNavigation?: boolean
    isLoginFlow?: boolean
    sx?: SxProps
    keepMounted?: boolean
}) => {
    const { markHistoryForOpen, closeAndGoBack } = useDrawerBackBehavior({
        isOpen: open,
        onClose
    })

    // Handle opening with history management (for swipe-to-open)
    const handleOpen = () => {
        if (enableBackNavigation) {
            markHistoryForOpen()
        }
        onOpen()
    }

    // Use either back-aware close or regular close
    const handleClose = enableBackNavigation ? closeAndGoBack : onClose

    // A top drawer's free edge is its bottom one, so the rounding and the grab handle
    // both belong there instead.
    const isTop = anchor === 'top'
    const grabHandle = !isLoginFlow && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pt: 1, pb: 1 }}>
            <Box sx={{ width: 40, height: 5, bgcolor: '#e0e0e0', borderRadius: 3 }} />
        </Box>
    )

    return (
        <MuiSwipeableDrawer
            keepMounted={keepMounted}
            anchor={anchor}
            open={open}
            onClose={handleClose}
            onOpen={handleOpen}
            slotProps={{
                paper: {
                    sx: {
                        ...(isTop
                            ? { borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }
                            : { borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }),
                        height: '90dvh',
                        maxHeight: '90dvh',
                        display: 'flex',
                        flexDirection: 'column',
                        ...sx
                    }
                }
            }}
            disableSwipeToOpen={disableSwipeToOpen}
        >
            {!isTop && grabHandle}

            {/* height:100% only for the fixed-height bottom sheet — a top drawer sizes to
                its content, where a percentage height has nothing to resolve against. */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    ...(isTop ? {} : { height: '100%' })
                }}
            >
                {children}
            </Box>

            {isTop && grabHandle}
        </MuiSwipeableDrawer>
    )
}

export default SwipeableDrawerWithBackNavigation
